import { defineStore } from 'pinia'
import { ref, reactive } from 'vue'
import { useAccountSettingsStore } from '@/stores/accountSettings'
import type { ListingRulesConfig } from '@/types/appTypes'
import { IApp } from '@/types'
import {
  fetchListingRules,
  updateListingRules,
  type ListingRulesData,
} from '@/services/listingRulesService'
import { ensureListingRuleIds } from '@/utils/listingRulesIds'

// Default publish rules (used if account config is missing/empty)
export const DEFAULT_LISTING_RULES: ListingRulesConfig = {
  all: {
    inventory_membership_rules: {
      ruleset: {
        logic: 'AND' as const,
        rules: [
          {
            type: 'group',
            logic: 'OR' as const,
            rules: [
              {
                type: 'rule',
                field: 'vin',
                operator: 'length_not_in',
                value: [17],
              },
            ],
            action: 'block',
            reason: 'VIN_INVALID_OR_MISSING',
          },
          {
            type: 'group',
            logic: 'OR' as const,
            rules: [
              { type: 'rule', field: 'make', operator: 'missing', value: true },
              { type: 'rule', field: 'model', operator: 'missing', value: true },
              { type: 'rule', field: 'year', operator: 'missing', value: true },
            ],
            action: 'block',
            reason: 'REQUIRED_FIELD_MISSING',
          },
        ],
      },
      evaluation: {
        mode: 'first_match',
        fallback_action: 'allow',
      },
    },
  },
}

export const useListingConfigStore = defineStore('listingConfig', () => {
  const accountSettings = useAccountSettingsStore()

  const listingConfig = ref<ListingRulesConfig | null>(null)
  const originalConfig = ref<ListingRulesConfig | null>(null)
  const usedDefaultListingRules = ref<boolean>(false)

  const loadingStatus = reactive<{
    config: IApp.ILoadingStatus
  }>({
    config: IApp.ILoadingStatus.Idle,
  })

  // Ensure we always have a usable config; if the config is
  // missing or empty, fall back to default publish rules.
  const ensureDefaultConfig = (listingRulesData: ListingRulesData | null): ListingRulesConfig => {
    // If no data provided, use defaults
    if (!listingRulesData) {
      console.log('📋 No publish rules found, using defaults')
      usedDefaultListingRules.value = true
      return DEFAULT_LISTING_RULES
    }

    // Check if the rules array is empty and apply defaults
    if (listingRulesData.ruleset?.rules?.length === 0) {
      console.warn('⚠️ Empty rules array detected, applying defaults')
      usedDefaultListingRules.value = true
      return DEFAULT_LISTING_RULES
    }

    // Wrap in 'all' segment structure for compatibility with UI
    const config: ListingRulesConfig = {
      all: {
        inventory_membership_rules: listingRulesData,
      },
    }

    usedDefaultListingRules.value = false
    return config
  }

  const getListingConfig = async () => {
    loadingStatus.config = IApp.ILoadingStatus.Loading

    try {
      const iseEndpoint = import.meta.env.VITE_ISE_BACKEND_URL
      const accountId = accountSettings.accountId

      if (!iseEndpoint || !accountId) {
        throw new Error('Missing ISE endpoint or account ID')
      }

      const response = await fetchListingRules(iseEndpoint, accountId)

      if (!response || !response.success) {
        throw new Error('Failed to fetch publish rules from ISE')
      }

      const ensuredConfig = ensureDefaultConfig(response.data.inventory_membership_rules)

      listingConfig.value = ensuredConfig
      originalConfig.value = ensuredConfig

      loadingStatus.config = IApp.ILoadingStatus.Success
      return ensuredConfig
    } catch (error) {
      console.error('❌ Error fetching listing config:', error)

      // On error, use defaults
      const defaultConfig = ensureDefaultConfig(null)
      listingConfig.value = defaultConfig
      originalConfig.value = defaultConfig

      loadingStatus.config = IApp.ILoadingStatus.Error
      return defaultConfig
    }
  }

  const saveListingConfig = async (config: ListingRulesConfig, isOnboarding = false) => {
    loadingStatus.config = IApp.ILoadingStatus.Loading

    try {
      const iseEndpoint = import.meta.env.VITE_ISE_BACKEND_URL
      const accountId = accountSettings.accountId
      const token = accountSettings.sessionToken

      if (!iseEndpoint || !accountId) {
        throw new Error('Missing ISE endpoint or account ID')
      }
      if (!token) {
        throw new Error('Missing session token (Authorization required)')
      }

      // Extract inventory_membership_rules from the 'all' segment wrapper
      const listingRulesData = config.all?.inventory_membership_rules
      if (!listingRulesData) {
        throw new Error('Invalid publish rules config structure')
      }

      // Phase 3: Ensure all listing rule nodes have stable IDs before saving
      // This enables future per-rule operations (add/remove/replace ops)
      if (listingRulesData.ruleset?.rules) {
        for (const rule of listingRulesData.ruleset.rules) {
          ensureListingRuleIds(rule)
        }
      }

      // Always use PUT (writes draft) regardless of onboarding status.
      // Onboarding completion happens via publish-drafts when user clicks "Finish".
      const success = await updateListingRules(iseEndpoint, accountId, {
        inventory_membership_rules: listingRulesData,
      }, token)
      
      // After successful save, mark as no longer using defaults
      if (success && usedDefaultListingRules.value) {
        usedDefaultListingRules.value = false
      }

      if (!success) {
        throw new Error('Failed to save publish rules')
      }

      // Update stored config after successful save
      listingConfig.value = config
      originalConfig.value = config

      loadingStatus.config = IApp.ILoadingStatus.Success
      return true
    } catch (error) {
      console.error('❌ Error saving listing config:', error)
      loadingStatus.config = IApp.ILoadingStatus.Error
      return false
    }
  }

  return {
    listingConfig,
    originalConfig,
    loadingStatus,
    getListingConfig,
    saveListingConfig,
    usedDefaultListingRules,
  }
})
